import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, Share2, MessageCircle, Instagram, Music2, Twitter, Lock, Clock, Ban, Loader2, Link2 } from "lucide-react";
import api, { API_BASE } from "@/lib/api";

const EXPIRY = [
  { v: 1, label: "1 día" },
  { v: 7, label: "7 días" },
  { v: 30, label: "30 días" },
  { v: 0, label: "Sin límite" },
];

export default function ShareDialog({ open, onOpenChange, project }) {
  const [expires, setExpires] = useState(7);
  const [password, setPassword] = useState("");
  const [shareId, setShareId] = useState(null);
  const [active, setActive] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [busy, setBusy] = useState(false);

  const url = shareId ? `${API_BASE}/share/${shareId}` : "";
  const text = `Mira lo que creé con Metáfora Play: "${project?.title || ""}"`;
  const enc = encodeURIComponent;

  const loadSettings = useCallback(async () => {
    if (!project?.id) return;
    try {
      const r = await api.get(`/projects/${project.id}/share`);
      setShareId(r.data.active ? r.data.share_id : null);
      setActive(r.data.active);
      setHasPassword(r.data.has_password);
      setExpiresAt(r.data.expires_at);
    } catch { /* noop */ }
  }, [project]);

  useEffect(() => { if (open) { setPassword(""); loadSettings(); } }, [open, loadSettings]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/share`, { expires_days: expires || null, password: password || null });
      setShareId(r.data.share_id);
      setActive(true);
      setHasPassword(r.data.has_password);
      setExpiresAt(r.data.expires_at);
      toast.success("Enlace listo");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear el enlace"); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/share/revoke`);
      setShareId(null); setActive(false); setHasPassword(false); setExpiresAt(null);
      toast.success("Enlace revocado — ya no funciona");
    } catch { toast.error("No se pudo revocar"); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Enlace copiado"); }
    catch { toast.message(url); }
  };
  const openWin = (u) => window.open(u, "_blank", "noopener,noreferrer");

  const nets = [
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#25D366", action: () => openWin(`https://wa.me/?text=${enc(text + " " + url)}`) },
    { id: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C", action: async () => { await copy(); openWin("https://www.instagram.com/"); } },
    { id: "tiktok", label: "TikTok", icon: Music2, color: "#69C9D0", action: async () => { await copy(); openWin("https://www.tiktok.com/"); } },
    { id: "x", label: "X", icon: Twitter, color: "#FFFFFF", action: () => openWin(`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`) },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#11131E] border-white/10 text-[#F8FAFC]" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Compartir "{project?.title}"</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">Los enlaces son privados: eliges cuánto duran y si piden contraseña. Puedes revocarlos cuando quieras.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="mono-label inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Caduca en</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {EXPIRY.map((o) => (
                <button key={o.v} data-testid={`share-expiry-${o.v}`} onClick={() => setExpires(o.v)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${expires === o.v ? "bg-[#FF334B] text-white" : "border border-white/12 hover:border-[#FF334B]"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mono-label inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Contraseña (opcional)</label>
            <input data-testid="share-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Sin contraseña" className="mt-2 w-full bg-[#0B0C13] border border-white/12 rounded-full px-4 py-2 text-sm focus:border-[#FF334B] outline-none" />
          </div>
          <button data-testid="share-generate-button" onClick={generate} disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-2.5 rounded-full transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 spin" /> : <Link2 className="w-4 h-4" />} {active ? "Actualizar enlace" : "Generar enlace"}
          </button>
        </div>

        {active && shareId && (
          <div className="space-y-3 pt-1" data-testid="share-active">
            <div className="text-xs text-[#94A3B8] flex flex-wrap gap-x-3 gap-y-1">
              {expiresAt ? <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Caduca {new Date(expiresAt).toLocaleDateString("es")}</span> : <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Sin caducidad</span>}
              {hasPassword && <span className="inline-flex items-center gap-1 text-[#FF9F2E]"><Lock className="w-3 h-3" /> Protegido con contraseña</span>}
            </div>
            <div className="flex items-center gap-2 bg-[#0B0C13] border border-white/10 rounded-xl px-3 py-2">
              <input readOnly value={url} data-testid="share-link-input" className="flex-1 bg-transparent text-sm text-[#94A3B8] outline-none truncate" />
              <button data-testid="share-copy-button" onClick={copy} className="shrink-0 text-[#FF334B] hover:text-[#E0223A]"><Copy className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {nets.map((n) => (
                <button key={n.id} data-testid={`share-net-${n.id}`} onClick={n.action}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl border border-white/10 hover:border-[#FF334B] transition-colors">
                  <n.icon className="w-6 h-6" style={{ color: n.color }} />
                  <span className="text-xs font-medium">{n.label}</span>
                </button>
              ))}
            </div>
            <button data-testid="share-revoke-button" onClick={revoke} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 border border-red-400/40 text-red-400 hover:bg-red-400/10 font-semibold py-2.5 rounded-full transition-colors">
              <Ban className="w-4 h-4" /> Revocar enlace
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
